import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowRight, Send, Building2, Lock, CalendarCheck, CheckCircle2, RefreshCw, Handshake, Package,
  ShieldCheck, Rocket, Cloud, Workflow, BarChart3, Sparkles, Code2, Brain, Server, PenTool, Zap, Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const wrap = "max-w-7xl mx-auto px-4 md:px-8 lg:px-12";
const h2 = "font-manrope font-bold text-3xl md:text-4xl text-slate-900";
const greenBtn = "bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full";

const comparison = [
  ["Ce que vous payez", "Du temps passé", "Un livrable"],
  ["Le budget", "Ouvert, il dérive", "Fixe, connu dès le départ"],
  ["La durée", "Indéfinie", "Des jalons datés"],
  ["Qui porte le risque", "Vous", "Nous"],
  ["La fin de mission", "Quand le budget est épuisé", "Quand le livrable est validé"],
];

const steps = [
  { title: "Cadrage (offert)", text: "En un échange de 30 à 60 minutes, nous clarifions votre besoin, même s'il est encore flou." },
  { title: "Proposition au forfait", text: "Vous recevez sous 48 h une proposition claire : les livrables, les jalons, le prix fixe." },
  { title: "Réalisation par jalons", text: "Le bon freelance, ou la bonne équipe, livre étape par étape. Vous voyez le projet avancer concrètement, pas des comptes rendus d'activité." },
  { title: "Recette et livraison", text: "Vous validez chaque livrable. Ce n'est pas conforme ? Nous corrigeons, sans surcoût." },
];

const packages = [
  { icon: ShieldCheck, title: "Audit technique ou sécurité", text: "Un rapport, les risques classés par priorité et un plan d'action." },
  { icon: Rocket, title: "MVP ou application web", text: "Un produit fonctionnel, mis en ligne, avec le code qui vous appartient." },
  { icon: Cloud, title: "Migration cloud", text: "Votre infrastructure migrée, documentée, avec une équipe formée." },
  { icon: Workflow, title: "Automatisation ou intégration", text: "Vos outils connectés et des workflows en production." },
  { icon: BarChart3, title: "Tableau de bord data", text: "Vos indicateurs clés, branchés sur vos données réelles." },
];

const commitments = [
  { icon: Lock, title: "Prix ferme", text: "Le prix convenu est le prix payé. Aucun dépassement." },
  { icon: CalendarCheck, title: "Délais engagés", text: "Chaque jalon a une date de livraison." },
  { icon: CheckCircle2, title: "Paiement au jalon", text: "Vous payez au fur et à mesure des livrables validés." },
  { icon: RefreshCw, title: "Corrections incluses", text: "Un livrable non conforme est repris sans frais." },
  { icon: Handshake, title: "Un interlocuteur unique", text: "Freelanceurs Connect pilote le projet de A à Z." },
  { icon: Package, title: "Propriété totale", text: "Le code, les documents et les accès vous appartiennent à la livraison." },
];

const expertises = [
  { icon: Code2, label: "Développement web et mobile" },
  { icon: Brain, label: "Data et IA" },
  { icon: Server, label: "Cloud et DevOps" },
  { icon: ShieldCheck, label: "Cybersécurité" },
  { icon: PenTool, label: "Product et UX" },
  { icon: Zap, label: "No-code et automatisation" },
];

const faqs = [
  { q: "Et si mon besoin évolue en cours de projet ?", a: "Nous chiffrons l'évolution à part, toujours au forfait. Vous décidez avant toute dépense supplémentaire." },
  { q: "Comment fixer un prix sur un besoin encore flou ?", a: "C'est le rôle du cadrage. Si le besoin est vraiment trop incertain, nous proposons d'abord un petit forfait de cadrage ou de prototype pour lever les inconnues." },
  { q: "Qui réalise le travail ?", a: "Des freelances IT sélectionnés dans notre réseau, pour leur expertise et leur fiabilité. Freelanceurs Connect reste votre interlocuteur et garant de la livraison." },
  { q: "Faites-vous de la régie ?", a: "Non. Nous vendons des résultats, pas des jours." },
];

const Hero = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="connect-hero">
    <div className={wrap}>
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
          <Building2 className="h-4 w-4 text-[#10b981]" /> Freelanceurs Connect · Projets IT au forfait
        </div>
        <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mb-6 animate-fade-in-up">
          Vous payez un <span className="text-[#10b981]">résultat</span>, pas des jours.
        </h1>
        <p className="text-base md:text-lg text-slate-600 mb-10">
          Freelanceurs Connect réalise vos projets IT au forfait, avec les meilleurs freelances de notre réseau. Un périmètre clair, un prix fixe, des livrables concrets.
        </p>
        <div className="flex flex-wrap gap-4">
          <a href="#connect-form" data-testid="connect-hero-cta">
            <Button className={`${greenBtn} px-8 py-6 text-base`}>Décrire mon projet <ArrowRight className="ml-2 h-5 w-5" /></Button>
          </a>
          <a href="#connect-process" data-testid="connect-hero-process-link">
            <Button variant="outline" className="rounded-full px-8 py-6 text-base border-2">Voir comment ça marche</Button>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Problem = () => (
  <section className="py-20 md:py-28 bg-slate-900" data-testid="connect-problem">
    <div className={`${wrap} grid lg:grid-cols-2 gap-12 items-center`}>
      <div>
        <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-6">La régie, vous connaissez.</h2>
        <p className="text-slate-300 text-base md:text-lg">
          Un consultant facturé au jour, une mission qui s'étire et un budget qui dérive. À la fin, beaucoup de temps passé… mais qu'est-ce qui a vraiment été livré ?
        </p>
      </div>
      <div className="space-y-4">
        <p className="text-slate-400 text-xl md:text-2xl font-manrope">Avec la régie, c'est <span className="text-white font-bold">vous</span> qui portez le risque.</p>
        <p className="text-[#52fbb0] text-2xl md:text-3xl font-manrope font-bold">Avec Freelanceurs Connect, c'est nous.</p>
      </div>
    </div>
  </section>
);

const Approach = () => (
  <section className="py-20 md:py-28 bg-white" data-testid="connect-approach">
    <div className={wrap}>
      <p className="text-sm font-semibold uppercase tracking-wider text-[#10b981] mb-3">Notre approche : le forfait</p>
      <h2 className={`${h2} mb-4`}>Un engagement sur le résultat</h2>
      <p className="text-slate-600 max-w-2xl mb-12">Avant de démarrer, nous définissons avec vous ce qui sera livré, quand et pour quel prix. Ensuite, c'est à nous de tenir l'engagement.</p>
      <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="connect-comparison-table">
        <table className="w-full text-xs sm:text-sm md:text-base">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="p-3 md:p-5" />
              <th className="p-3 md:p-5 font-semibold text-slate-500">Régie classique</th>
              <th className="p-3 md:p-5 font-bold text-slate-900 bg-[#52fbb0]/20">Freelanceurs Connect</th>
            </tr>
          </thead>
          <tbody>
            {comparison.map(([label, regie, connect]) => (
              <tr key={label} className="border-t border-slate-200">
                <td className="p-3 md:p-5 font-medium text-slate-900">{label}</td>
                <td className="p-3 md:p-5 text-slate-500">{regie}</td>
                <td className="p-3 md:p-5 font-semibold text-slate-900 bg-[#52fbb0]/10">{connect}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section id="connect-process" className="py-20 md:py-28 bg-slate-50" data-testid="connect-process">
    <div className={wrap}>
      <h2 className={`${h2} mb-12`}>4 étapes, zéro surprise</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        {steps.map((s, i) => (
          <div key={s.title} className="border-l-4 border-[#52fbb0] pl-6" data-testid={`connect-step-${i + 1}`}>
            <span className="font-manrope font-bold text-5xl text-[#52fbb0]">0{i + 1}</span>
            <h3 className="font-manrope font-semibold text-xl text-slate-900 mt-4 mb-3">{s.title}</h3>
            <p className="text-slate-600 text-sm md:text-base">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const IconCard = ({ icon: Icon, title, text, children, testId }) => (
  <Card className="border-0 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={testId}>
    <CardContent className="p-8">
      <div className="w-12 h-12 rounded-xl bg-[#52fbb0]/20 flex items-center justify-center mb-6">
        <Icon className="h-6 w-6 text-[#10b981]" />
      </div>
      <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-3">{title}</h3>
      <p className="text-slate-600 text-sm md:text-base">{text}</p>
      {children}
    </CardContent>
  </Card>
);

const Packages = () => (
  <section className="py-20 md:py-28 bg-white" data-testid="connect-packages">
    <div className={wrap}>
      <p className="text-sm font-semibold uppercase tracking-wider text-[#10b981] mb-3">Exemples de forfaits</p>
      <h2 className={`${h2} mb-12`}>Des livrables concrets, à prix connu</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((p, i) => (
          <IconCard key={p.title} {...p} testId={`connect-package-${i}`}>
            <p className="mt-6 text-sm font-semibold text-slate-900">À partir de : <span className="text-[#10b981]">Sur devis</span></p>
          </IconCard>
        ))}
        <a href="#connect-form" className="rounded-xl bg-slate-900 p-8 flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300" data-testid="connect-package-custom">
          <Sparkles className="h-8 w-8 text-[#52fbb0]" />
          <div>
            <p className="font-manrope font-semibold text-xl text-white mt-6 mb-3">Votre besoin ne rentre dans aucune case ?</p>
            <p className="text-slate-300 text-sm md:text-base">Nous construisons un forfait sur mesure.</p>
            <span className="inline-flex items-center gap-2 text-[#52fbb0] font-semibold mt-6">En parler <ArrowRight className="h-4 w-4" /></span>
          </div>
        </a>
      </div>
    </div>
  </section>
);

const Commitments = () => (
  <section className="py-20 md:py-28 bg-slate-50" data-testid="connect-commitments">
    <div className={wrap}>
      <p className="text-sm font-semibold uppercase tracking-wider text-[#10b981] mb-3">Nos engagements</p>
      <h2 className={`${h2} mb-12`}>Ce que nous vous garantissons</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {commitments.map((c, i) => <IconCard key={c.title} {...c} testId={`connect-commitment-${i}`} />)}
      </div>
    </div>
  </section>
);

const Expertises = () => (
  <section className="py-20 md:py-28 bg-slate-900" data-testid="connect-expertises">
    <div className={wrap}>
      <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-12">Expertises</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {expertises.map(({ icon: Icon, label }, i) => (
          <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#52fbb0]/60 transition-colors" data-testid={`connect-expertise-${i}`}>
            <Icon className="h-5 w-5 text-[#52fbb0] shrink-0" />
            <span className="text-white text-sm md:text-base font-medium">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section className="py-20 md:py-28 bg-white" data-testid="connect-faq">
    <div className={`${wrap} max-w-4xl`}>
      <h2 className={`${h2} mb-10`}>Questions fréquentes</h2>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`} className="border rounded-xl px-6" data-testid={`connect-faq-${i}`}>
            <AccordionTrigger className="text-left font-semibold text-slate-900">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

const useLeadForm = (initial, toPayload, successMsg, onDone) => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, toPayload(form));
      toast.success(successMsg);
      setForm(initial);
      onDone?.();
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };
  return { form, set, submit, sending };
};

const ProjectForm = () => {
  const { form, set, submit, sending } = useLeadForm(
    { name: "", company: "", email: "", phone: "", need: "" },
    (f) => ({ name: f.name, email: f.email, phone: f.phone || null, subject: `[B2B Connect] ${f.company}`, message: `Entreprise : ${f.company}\n\nProjet : ${f.need}` }),
    "Demande envoyée ! Nous revenons vers vous pour planifier votre cadrage offert.",
  );
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="connect-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="c-name" label="Nom complet *"><Input id="c-name" required value={form.name} onChange={set("name")} data-testid="connect-input-name" /></Field>
        <Field id="c-company" label="Entreprise *"><Input id="c-company" required value={form.company} onChange={set("company")} data-testid="connect-input-company" /></Field>
        <Field id="c-email" label="Email professionnel *"><Input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="connect-input-email" /></Field>
        <Field id="c-phone" label="Téléphone"><Input id="c-phone" value={form.phone} onChange={set("phone")} data-testid="connect-input-phone" /></Field>
      </div>
      <Field id="c-need" label="Votre projet en quelques lignes *"><Textarea id="c-need" required rows={5} value={form.need} onChange={set("need")} data-testid="connect-input-need" /></Field>
      <Button type="submit" disabled={sending} className={`w-full ${greenBtn} py-6 text-base`} data-testid="connect-submit-btn">
        <Send className="mr-2 h-5 w-5" /> {sending ? "Envoi..." : "Réserver mon cadrage offert"}
      </Button>
    </form>
  );
};

const FinalCta = () => (
  <section id="connect-form" className="py-20 md:py-28 bg-slate-50" data-testid="connect-cta">
    <div className={`${wrap} grid lg:grid-cols-2 gap-12 items-start`}>
      <div>
        <h2 className={`${h2} mb-6`}>Un projet IT ? <span className="text-[#10b981]">Recevez une proposition au forfait sous 48 h.</span></h2>
        <p className="text-slate-600 text-base md:text-lg">Décrivez votre besoin, même s'il est encore flou. Nous vous recontactons pour un cadrage offert de 30 à 60 minutes.</p>
      </div>
      <Card className="border-0 shadow-lg"><CardContent className="p-8"><ProjectForm /></CardContent></Card>
    </div>
  </section>
);

const FreelanceForm = ({ onDone }) => {
  const { form, set, submit, sending } = useLeadForm(
    { name: "", email: "", phone: "", expertise: "", linkedin: "" },
    (f) => ({ name: f.name, email: f.email, phone: f.phone || null, subject: `[Freelance Connect] ${f.expertise}`, message: `Expertise : ${f.expertise}\nLinkedIn / Portfolio : ${f.linkedin || "-"}` }),
    "Candidature envoyée ! Nous revenons vers vous rapidement.",
    onDone,
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="freelance-form">
      <Field id="f-name" label="Nom complet *"><Input id="f-name" required value={form.name} onChange={set("name")} data-testid="freelance-input-name" /></Field>
      <Field id="f-email" label="Email *"><Input id="f-email" type="email" required value={form.email} onChange={set("email")} data-testid="freelance-input-email" /></Field>
      <Field id="f-phone" label="Téléphone"><Input id="f-phone" value={form.phone} onChange={set("phone")} data-testid="freelance-input-phone" /></Field>
      <Field id="f-exp" label="Expertise principale *"><Input id="f-exp" required placeholder="Ex : Développeur React senior" value={form.expertise} onChange={set("expertise")} data-testid="freelance-input-expertise" /></Field>
      <Field id="f-li" label="LinkedIn ou portfolio"><Input id="f-li" value={form.linkedin} onChange={set("linkedin")} data-testid="freelance-input-linkedin" /></Field>
      <Button type="submit" disabled={sending} className={`w-full ${greenBtn} py-5`} data-testid="freelance-submit-btn">
        {sending ? "Envoi..." : "Envoyer ma candidature"}
      </Button>
    </form>
  );
};

const FreelanceBlock = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="py-16 bg-white" data-testid="connect-freelance-block">
      <div className={wrap}>
        <div className="rounded-2xl border-2 border-[#52fbb0] p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8 justify-between">
          <div className="flex gap-4 max-w-3xl">
            <Users className="h-8 w-8 text-[#10b981] shrink-0" />
            <p className="text-slate-700 text-base md:text-lg">
              Vous êtes <strong>freelance IT</strong> et préférez être jugé sur vos résultats plutôt que sur vos heures ? Rejoignez le réseau Freelanceurs Connect.
            </p>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 font-semibold shrink-0" data-testid="connect-join-network-btn">
            Rejoindre le réseau <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="freelance-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Rejoindre le réseau</DialogTitle>
            <DialogDescription>Présentez-vous en quelques mots, notre équipe revient vers vous.</DialogDescription>
          </DialogHeader>
          <FreelanceForm onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default function ConnectPage() {
  return (
    <div data-testid="connect-page">
      <Hero />
      <Problem />
      <Approach />
      <Process />
      <Packages />
      <Commitments />
      <Expertises />
      <Faq />
      <FinalCta />
      <FreelanceBlock />
    </div>
  );
}
